// pages/admin/DataGeneration.tsx
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../store/store";
import { fetchUsers } from "../../store/slices/adminSlice";
import { generateHistoricalData } from "../../store/slices/adminSlice";
import GlobalStyle from "../../styles/GlobalStyle";
import {
  ChartBarIcon,
  CalendarIcon,
  UserIcon,
  PlayIcon,
  InformationCircleIcon,
  QuestionMarkCircleIcon,
} from "@heroicons/react/24/outline";
import { User } from "../../types";

const AdminDataGeneration: React.FC = () => {
  const SimpleIcon = () => (
    <svg
      className="h-5 w-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );

  const ComplexIcon = () => (
    <svg
      className="h-5 w-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z"
      />
    </svg>
  );

  const dispatch = useDispatch<AppDispatch>();
  const { users = [], isLoading } = useSelector(
    (state: RootState) => state.admin
  );
  const [generateAdUnits, setGenerateAdUnits] = useState(false);
  const [adUnitsCount, setAdUnitsCount] = useState(5);
  const [mode, setMode] = useState<"simple" | "complex">("simple");
  const [showHelp, setShowHelp] = useState(false);
  const [selectedUser, setSelectedUser] = useState<string>("");
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split("T")[0],
    end: new Date().toISOString().split("T")[0],
  });

  // Simple mode settings
  const [simpleSettings, setSimpleSettings] = useState({
    totalRevenue: 10000,
    averageDailyRevenue: 1000,
    totalPageViews: 50000,
    averageCTR: 2.5,
  });

  // Complex mode settings (existing)
  const [complexSettings, setComplexSettings] = useState({
    initial_revenue: 100,
    daily_growth_rate: 3,
    initial_page_views: 5000,
    page_views_growth_rate: 2,
    ctr: 2.5,
    cpc: 1.5,
    cpm: 4.0,
  });

  const [sourceDistribution, setSourceDistribution] = useState({
    search: 40,
    display: 30,
    video: 15,
    mobile: 10,
    native: 5,
  });

  const [isGenerating, setIsGenerating] = useState(false);
  const [generationLog, setGenerationLog] = useState<string[]>([]);

  useEffect(() => {
    dispatch(fetchUsers({}));
  }, [dispatch]);

  const addLog = (message: string) => {
    setGenerationLog((prev) => [
      `[${new Date().toLocaleTimeString()}] ${message}`,
      ...prev.slice(0, 9),
    ]);
  };

  const handleGenerate = async () => {
    if (!selectedUser) {
      addLog("Error: Please select a user");
      return;
    }

    setIsGenerating(true);
    addLog("Starting data generation...");

    try {
      const data: any = {
        user_id: parseInt(selectedUser),
        start_date: dateRange.start,
        end_date: dateRange.end,
        mode: mode, // Make sure this is included
      };

      if (mode === "simple") {
        // Simple mode - send simple parameters
        data.total_revenue = simpleSettings.totalRevenue;
        data.total_page_views = simpleSettings.totalPageViews;
        data.average_ctr = simpleSettings.averageCTR;
        data.trend = "growing"; // Default trend
      } else {
        // Complex mode - send settings object
        data.settings = {
          initial_revenue: complexSettings.initial_revenue,
          daily_growth_rate: complexSettings.daily_growth_rate,
          initial_page_views: complexSettings.initial_page_views,
          page_views_growth_rate: complexSettings.page_views_growth_rate,
          ctr: complexSettings.ctr,
          cpc: complexSettings.cpc,
          cpm: complexSettings.cpm,
          source_distribution: sourceDistribution,
        };
      }

      // Add optional parameters
      if (generateAdUnits) {
        data.generate_ad_units = true;
        data.ad_units_count = adUnitsCount;
      }

      console.log("Sending data:", data); // Debug log

      const result = await dispatch(generateHistoricalData(data));

      if (result.meta.requestStatus === "fulfilled") {
        const payload = result.payload as any;
        addLog(
          `Successfully generated ${
            payload.records_generated?.metrics || 0
          } metrics records`
        );
        addLog("✅ Data generation completed successfully");
      } else {
        addLog("❌ Data generation failed");
      }
    } catch (error: any) {
      addLog(`Error: ${error.message || "Unknown error"}`);
      console.error("Generation error:", error);
    } finally {
      setIsGenerating(false);
    }
  };

  const calculateDays = () => {
    const start = new Date(dateRange.start);
    const end = new Date(dateRange.end);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  };

  const estimatedRecords = calculateDays() * 5;
  const safeUsers = Array.isArray(users) ? users : [];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className={GlobalStyle.heading1}>Data Generation</h1>
          <p className={GlobalStyle.bodyText}>
            Generate sample data for testing and demonstration
          </p>
        </div>

        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowHelp(!showHelp)}
            className="p-2 border border-[#dadce0] text-[#1a73e8] rounded-lg hover:bg-[#f8f9fa] transition-colors"
          >
            <QuestionMarkCircleIcon className="h-5 w-5" />
          </button>

          <button
            onClick={handleGenerate}
            disabled={isGenerating || !selectedUser}
            className={`flex items-center space-x-2 px-4 py-2 bg-[#1a73e8] text-white rounded-lg hover:bg-[#0d62d9] transition-colors ${
              isGenerating || !selectedUser
                ? "opacity-50 cursor-not-allowed"
                : ""
            }`}
          >
            <PlayIcon className="h-5 w-5" />
            <span>{isGenerating ? "Generating..." : "Generate Data"}</span>
          </button>
        </div>
      </div>

      {/* Help Modal */}
      {showHelp && (
        <div className={GlobalStyle.alertInfo}>
          <div className="flex items-start space-x-3">
            <InformationCircleIcon className="h-5 w-5 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="font-medium mb-2">How to generate data</h4>
              <ul className="text-sm space-y-1">
                <li>
                  • <strong>Simple Mode:</strong> Just enter the total amounts
                  you want - we'll automatically distribute them over time
                </li>
                <li>
                  • <strong>Complex Mode:</strong> Control every parameter
                  including daily growth rates
                </li>
                <li>• Select a user and date range first</li>
                <li>
                  • Generated data will overwrite existing data for the selected
                  period
                </li>
                <li>• You can generate data for any user in the system</li>
              </ul>
              <button
                onClick={() => setShowHelp(false)}
                className="mt-3 text-sm text-blue-600 hover:text-blue-700"
              >
                Got it, thanks!
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mode Selection */}
      <div className="flex space-x-4">
        <button
          onClick={() => setMode("simple")}
          className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-lg border ${
            mode === "simple"
              ? "border-blue-500 bg-blue-50 text-blue-600"
              : "border-gray-300 hover:bg-gray-50"
          }`}
        >
          <SimpleIcon />
          <span>Simple Mode</span>
        </button>

        <button
          onClick={() => setMode("complex")}
          className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-lg border ${
            mode === "complex"
              ? "border-blue-500 bg-blue-50 text-blue-600"
              : "border-gray-300 hover:bg-gray-50"
          }`}
        >
          <ComplexIcon />
          <span>Complex Mode</span>
        </button>
      </div>

      {/* User Selection & Date Range */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className={GlobalStyle.card}>
          <div className="p-4 border-b border-gray-200">
            <h3 className="font-medium text-gray-800">User Selection</h3>
          </div>
          <div className="p-4">
            <select
              value={selectedUser}
              onChange={(e) => setSelectedUser(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded"
            >
              <option value="">Select a user...</option>
              {safeUsers.map((user: any) => (
                <option key={user.id} value={user.id}>
                  {user.name} ({user.email})
                </option>
              ))}
            </select>

            {selectedUser && (
              <div className="mt-4 p-3 bg-blue-50 rounded">
                <div className="flex items-center space-x-3">
                  <UserIcon className="h-5 w-5 text-blue-600" />
                  <div>
                    <p className="font-medium">
                      {
                        safeUsers.find(
                          (u: User) => u.id.toString() === selectedUser
                        )?.name
                      }
                    </p>
                    <p className="text-sm text-gray-600">
                      Will generate {calculateDays()} days of data
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className={GlobalStyle.card}>
          <div className="p-4 border-b border-gray-200">
            <h3 className="font-medium text-gray-800">Date Range</h3>
          </div>
          <div className="p-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Date
              </label>
              <div className="relative">
                <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) =>
                    setDateRange({ ...dateRange, start: e.target.value })
                  }
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Date
              </label>
              <div className="relative">
                <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) =>
                    setDateRange({ ...dateRange, end: e.target.value })
                  }
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Settings based on mode */}
      <div className={GlobalStyle.card}>
        <div className="p-4 border-b border-gray-200">
          <h3 className="font-medium text-gray-800">
            {mode === "simple" ? "Simple Settings" : "Advanced Settings"}
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            {mode === "simple"
              ? "Just enter the total amounts - we'll handle the details"
              : "Fine-tune every parameter"}
          </p>
        </div>

        <div className="p-4">
          {mode === "simple" ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Total Revenue ($)
                </label>
                <input
                  type="number"
                  value={simpleSettings.totalRevenue}
                  onChange={(e) =>
                    setSimpleSettings({
                      ...simpleSettings,
                      totalRevenue: parseFloat(e.target.value) || 0,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded"
                  placeholder="e.g., 10000"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Total revenue for entire period
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Total Page Views
                </label>
                <input
                  type="number"
                  value={simpleSettings.totalPageViews}
                  onChange={(e) =>
                    setSimpleSettings({
                      ...simpleSettings,
                      totalPageViews: parseInt(e.target.value) || 0,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded"
                  placeholder="e.g., 50000"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Total page views for entire period
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Average CTR (%)
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={simpleSettings.averageCTR}
                  onChange={(e) =>
                    setSimpleSettings({
                      ...simpleSettings,
                      averageCTR: parseFloat(e.target.value) || 0,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded"
                  placeholder="e.g., 2.5"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Click-through rate percentage
                </p>
              </div>

              <div className="bg-blue-50 p-4 rounded">
                <p className="text-sm font-medium text-blue-900">Preview</p>
                <p className="text-xs text-blue-700 mt-1">
                  {calculateDays()} days × ~
                  {(simpleSettings.totalRevenue / calculateDays()).toFixed(2)}{" "}
                  daily revenue
                </p>
                <p className="text-xs text-blue-700">
                  {estimatedRecords} total records will be generated
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Complex settings form (existing complex mode) */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Initial Revenue ($)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={complexSettings.initial_revenue}
                      onChange={(e) =>
                        setComplexSettings({
                          ...complexSettings,
                          initial_revenue: parseFloat(e.target.value) || 0,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Daily Growth Rate (%)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={complexSettings.daily_growth_rate}
                      onChange={(e) =>
                        setComplexSettings({
                          ...complexSettings,
                          daily_growth_rate: parseFloat(e.target.value) || 0,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Initial Page Views
                    </label>
                    <input
                      type="number"
                      value={complexSettings.initial_page_views}
                      onChange={(e) =>
                        setComplexSettings({
                          ...complexSettings,
                          initial_page_views: parseInt(e.target.value) || 0,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded"
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      CTR (%)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={complexSettings.ctr}
                      onChange={(e) =>
                        setComplexSettings({
                          ...complexSettings,
                          ctr: parseFloat(e.target.value) || 0,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      CPC ($)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={complexSettings.cpc}
                      onChange={(e) =>
                        setComplexSettings({
                          ...complexSettings,
                          cpc: parseFloat(e.target.value) || 0,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      CPM ($)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={complexSettings.cpm}
                      onChange={(e) =>
                        setComplexSettings({
                          ...complexSettings,
                          cpm: parseFloat(e.target.value) || 0,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded"
                    />
                  </div>
                </div>
              </div>

              {/* Revenue Source Distribution */}
              <div>
                <h4 className="font-medium text-gray-800 mb-3">
                  Revenue Source Distribution
                </h4>
                <div className="space-y-3">
                  {Object.entries(sourceDistribution).map(
                    ([source, percentage]) => (
                      <div key={source} className="flex items-center space-x-4">
                        <span className="w-24 text-sm capitalize">
                          {source}
                        </span>
                        <div className="flex-1">
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={percentage}
                            onChange={(e) => {
                              const newValue = parseInt(e.target.value);
                              setSourceDistribution({
                                ...sourceDistribution,
                                [source]: newValue,
                              });
                            }}
                            className="w-full h-2 bg-gray-200 rounded"
                          />
                        </div>
                        <div className="w-20">
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={percentage}
                            onChange={(e) => {
                              const newValue = parseInt(e.target.value) || 0;
                              setSourceDistribution({
                                ...sourceDistribution,
                                [source]: newValue,
                              });
                            }}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-center"
                          />
                        </div>
                        <span className="w-12 text-right text-sm">
                          {percentage}%
                        </span>
                      </div>
                    )
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Generation Log */}
      <div className={GlobalStyle.card}>
        <div className="p-4 border-b border-gray-200">
          <h3 className="font-medium text-gray-800">Generation Log</h3>
        </div>

        <div className="p-4">
          <div className="h-64 bg-gray-50 rounded overflow-y-auto p-3">
            {generationLog.length === 0 ? (
              <div className="h-full flex items-center justify-center text-gray-500">
                <div className="text-center">
                  <ChartBarIcon className="h-8 w-8 mx-auto mb-2" />
                  <p>Logs will appear here during generation</p>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                {generationLog.map((log, index) => (
                  <div
                    key={index}
                    className={`p-2 rounded text-sm font-mono ${
                      log.includes("✅") || log.includes("Success")
                        ? "bg-green-50 text-green-700"
                        : log.includes("❌") || log.includes("Error")
                        ? "bg-red-50 text-red-700"
                        : "bg-gray-100 text-gray-700"
                    }`}
                  >
                    {log}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDataGeneration;
